import { JSX, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useInput } from 'react-admin';
import { downloadCsv } from '../../../import/csv-import';
import { keyProblems, KeyValuePair, pairsToCsv, problemSummary, toPair } from './key-value';
import { KeyValueImportDialog } from './KeyValueImportDialog';

/** A pair as the table holds it. `rowId` keeps React's rows stable across deletes; it is never saved. */
interface EditorRow extends KeyValuePair {
    rowId: number;
}

const stripIds = (rows: EditorRow[]): KeyValuePair[] =>
    rows.map((row: EditorRow): KeyValuePair => ({ key: row.key, value: row.value }));

const asPairs = (value: unknown): KeyValuePair[] => Array.isArray(value) ? value.map(toPair) : [];

/** Refuses the save while any key is empty or repeated; the table shows which. */
const validateKeys = (value: unknown): string | undefined =>
    keyProblems(asPairs(value)).size > 0 ? 'Every key must be filled in and unique' : undefined;

/**
 * The editor for a `key/value` setting: an editable table, Import CSV and Download CSV.
 *
 * Built on `useInput`, so it saves through the form like any other input but draws none of
 * react-admin's validation UI; the messages below are its own. The validator still refuses the
 * save, so a duplicate cannot be written even if the messages were missed.
 */
export const KeyValueEditor: (props: { fileName: string }) => JSX.Element =
    ({ fileName }: { fileName: string }): JSX.Element => {
        const { field } = useInput({ source: 'value', validate: validateKeys });
        const nextRowId = useRef<number>(1);
        const withIds = (pairs: KeyValuePair[]): EditorRow[] =>
            pairs.map((pair: KeyValuePair): EditorRow => ({ ...pair, rowId: nextRowId.current++ }));

        // Read once: the form holds the record already, and from here on the table is the source.
        const [rows, setRows] = useState<EditorRow[]>((): EditorRow[] => withIds(asPairs(field.value)));
        const [importing, setImporting] = useState<boolean>(false);
        const lastKeyInput = useRef<HTMLInputElement | null>(null);

        const problems: Map<number, string> = useMemo((): Map<number, string> => keyProblems(rows), [rows]);
        const summary: string | null = problemSummary(problems);

        function change(next: EditorRow[]): void {
            setRows(next);
            field.onChange(stripIds(next));
        }

        function edit(rowId: number, side: 'key' | 'value', text: string): void {
            change(rows.map((row: EditorRow): EditorRow => row.rowId === rowId ? { ...row, [side]: text } : row));
        }

        function addRow(): void {
            change([...rows, ...withIds([{ key: '', value: '' }])]);
            // After the render that adds it.
            setTimeout((): void => lastKeyInput.current?.focus(), 0);
        }

        return (
            <Box>
                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' } }>
                    <Typography sx={ { fontSize: 13, color: 'text.secondary', mr: 'auto' } }>
                        { rows.length === 1 ? '1 pair' : rows.length + ' pairs' }
                    </Typography>
                    <Button size="small" startIcon={ <FileUploadOutlinedIcon /> } onClick={ (): void => setImporting(true) }>
                        Import CSV
                    </Button>
                    <Button
                        size="small"
                        startIcon={ <FileDownloadOutlinedIcon /> }
                        onClick={ (): void => downloadCsv(fileName + '.csv', pairsToCsv(stripIds(rows))) }
                    >
                        Download CSV
                    </Button>
                </Box>

                { summary !== null &&
                    <Alert severity="error" variant="outlined" sx={ { mb: 1.5, fontSize: 13 } } role="status">
                        { summary }
                    </Alert>
                }

                <Table size="small" sx={ { tableLayout: 'fixed', '& td, & th': { borderColor: 'divider' } } }>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={ { width: 48, color: 'text.secondary' } }>#</TableCell>
                            <TableCell sx={ { width: '35%' } }>Key</TableCell>
                            <TableCell>Value</TableCell>
                            <TableCell sx={ { width: 56 } } />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        { rows.map((row: EditorRow, index: number): JSX.Element => {
                            const problem: string | undefined = problems.get(index);
                            return (
                                <TableRow key={ row.rowId }>
                                    <TableCell sx={ { color: 'text.secondary', fontSize: 12, verticalAlign: 'top', pt: 2 } }>
                                        { index + 1 }
                                    </TableCell>
                                    <TableCell sx={ { verticalAlign: 'top' } }>
                                        <TextField
                                            value={ row.key }
                                            onChange={ (event): void => edit(row.rowId, 'key', event.target.value) }
                                            inputRef={ index === rows.length - 1 ? lastKeyInput : undefined }
                                            error={ problem !== undefined }
                                            helperText={ problem }
                                            size="small"
                                            fullWidth
                                            slotProps={ { htmlInput: { 'aria-label': 'Key, row ' + (index + 1) } } }
                                        />
                                    </TableCell>
                                    <TableCell sx={ { verticalAlign: 'top' } }>
                                        <TextField
                                            value={ row.value }
                                            onChange={ (event): void => edit(row.rowId, 'value', event.target.value) }
                                            size="small"
                                            fullWidth
                                            multiline
                                            maxRows={ 6 }
                                            slotProps={ { htmlInput: { 'aria-label': 'Value, row ' + (index + 1) } } }
                                        />
                                    </TableCell>
                                    <TableCell sx={ { verticalAlign: 'top' } }>
                                        <IconButton
                                            size="small"
                                            aria-label={ 'Delete row ' + (index + 1) }
                                            onClick={ (): void => change(rows.filter((other: EditorRow): boolean => other.rowId !== row.rowId)) }
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        }) }
                        { rows.length === 0 &&
                            <TableRow>
                                <TableCell colSpan={ 4 } sx={ { color: 'text.secondary', fontSize: 13, py: 3, textAlign: 'center' } }>
                                    No pairs yet. Add one, or import a CSV.
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>

                <Button size="small" startIcon={ <AddIcon /> } onClick={ addRow } sx={ { mt: 1.5 } }>
                    Add pair
                </Button>

                <KeyValueImportDialog
                    open={ importing }
                    existing={ stripIds(rows) }
                    onClose={ (): void => setImporting(false) }
                    onApply={ (pairs: KeyValuePair[]): void => {
                        change(withIds(pairs));
                        setImporting(false);
                    } }
                />
            </Box>
        );
    };
